import React, { useState, useEffect, useMemo } from 'react';
import type { Reservation, Table, Event } from '../../types';
import {
  getTablesFreeForSlot,
  getTablesFittingParty,
  getTotalAvailableCapacity,
  getMaxSingleTableCapacity,
  tableFitsParty,
  eventAppliesToReservationDate,
  RESERVATION_TABLE_BUFFER_MINUTES,
} from '../../lib/reservationCapacity';
import { useSoftKeyboardField } from '../../hooks/useSoftKeyboardField';

interface ReservationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (reservation: Omit<Reservation, 'id' | 'createdAt' | 'updatedAt'>) => void;
  reservation?: Reservation | null;
  tables: Table[];
  reservations: Reservation[];
  events: Event[];
}

const ReservationModal: React.FC<ReservationModalProps> = ({
  isOpen,
  onClose,
  onSave,
  reservation,
  tables,
  reservations,
  events,
}) => {
  const [formData, setFormData] = useState({
    eventId: reservation?.eventId ?? '',
    tableId: reservation?.tableId ?? 0,
    assignedTableIds: (reservation?.assignedTableIds ?? []) as number[],
    customerName: reservation?.customerName ?? '',
    customerPhone: reservation?.customerPhone ?? '',
    customerEmail: reservation?.customerEmail ?? '',
    partySize: reservation?.partySize ?? 2,
    reservationDate: reservation?.reservationDate ?? new Date().toISOString().split('T')[0],
    reservationTime: reservation?.reservationTime ?? '18:00',
    status: reservation?.status ?? 'pending',
    specialRequests: reservation?.specialRequests ?? '',
    notes: reservation?.notes ?? '',
    requiresMerge: reservation?.requiresMerge ?? false,
    slotId: reservation?.slotId ?? '',
    adultsCountStr:
      reservation?.adultsCount != null ? String(reservation.adultsCount) : '',
    kidsBelow10: reservation?.kidsBelow10Count != null ? String(reservation.kidsBelow10Count) : '',
    kids10Plus: reservation?.kidsAbove10Count != null ? String(reservation.kidsAbove10Count) : '',
    foodAllergy: (reservation?.foodAllergyDeclaration ?? 'none') as 'none' | 'has_allergies',
    allergyDetails: reservation?.allergyDetails ?? '',
  });
  const [capacityChecked, setCapacityChecked] = useState(false);
  const [saveMode, setSaveMode] = useState<'table' | 'merge' | 'waitlist' | null>(null);
  const [partySizeInput, setPartySizeInput] = useState(() =>
    String(reservation?.partySize ?? 2),
  );

  const commitPartySizeInput = () => {
    const n = parseInt(partySizeInput, 10);
    const size = Number.isFinite(n) && n >= 1 ? Math.min(99, n) : 1;
    setPartySizeInput(String(size));
    if (size !== formData.partySize) {
      setFormData((prev) => ({ ...prev, partySize: size }));
      setCapacityChecked(false);
      setSaveMode(null);
    }
  };

  const partySizeKeyboard = useSoftKeyboardField({
    id: 'event-reservation-party-size',
    type: 'numeric',
    label: 'Party size',
    value: partySizeInput,
    onChange: (v) => {
      if (!(v === '' || /^\d{1,2}$/.test(v))) return;
      setPartySizeInput(v);
      if (v === '') return;
      const n = parseInt(v, 10);
      if (!Number.isFinite(n) || n < 1) return;
      const size = Math.min(99, n);
      setFormData((prev) => ({ ...prev, partySize: size }));
      setCapacityChecked(false);
      setSaveMode(null);
    },
    manualValidator: (v) => v === '' || /^\d{1,2}$/.test(v),
  });

  useEffect(() => {
    if (reservation) {
      setFormData({
        eventId: reservation.eventId ?? '',
        tableId: reservation.tableId ?? 0,
        assignedTableIds: reservation.assignedTableIds ?? [],
        customerName: reservation.customerName,
        customerPhone: reservation.customerPhone,
        customerEmail: reservation.customerEmail ?? '',
        partySize: reservation.partySize,
        reservationDate: reservation.reservationDate,
        reservationTime: reservation.reservationTime,
        status: reservation.status,
        specialRequests: reservation.specialRequests ?? '',
        notes: reservation.notes ?? '',
        requiresMerge: reservation.requiresMerge ?? false,
        slotId: reservation.slotId ?? '',
        adultsCountStr:
          reservation.adultsCount != null ? String(reservation.adultsCount) : '',
        kidsBelow10:
          reservation.kidsBelow10Count != null ? String(reservation.kidsBelow10Count) : '',
        kids10Plus:
          reservation.kidsAbove10Count != null ? String(reservation.kidsAbove10Count) : '',
        foodAllergy: reservation.foodAllergyDeclaration ?? 'none',
        allergyDetails: reservation.allergyDetails ?? '',
      });
      setPartySizeInput(String(reservation.partySize));
      setCapacityChecked(false);
      setSaveMode(null);
    } else {
      setFormData({
        eventId: '',
        tableId: 0,
        assignedTableIds: [],
        customerName: '',
        customerPhone: '',
        customerEmail: '',
        partySize: 2,
        reservationDate: new Date().toISOString().split('T')[0],
        reservationTime: '18:00',
        status: 'pending',
        specialRequests: '',
        notes: '',
        requiresMerge: false,
        slotId: '',
        adultsCountStr: '',
        kidsBelow10: '',
        kids10Plus: '',
        foodAllergy: 'none',
        allergyDetails: '',
      });
      setPartySizeInput('2');
      setCapacityChecked(false);
      setSaveMode(null);
    }
  }, [reservation, isOpen]);

  const selectedEvent = useMemo(
    () => events.find((e) => String(e.id) === String(formData.eventId)),
    [events, formData.eventId]
  );
  const eventMaxCapacity = selectedEvent?.maxCapacity;

  const freeTables = useMemo(
    () =>
      getTablesFreeForSlot(
        tables,
        reservations,
        formData.reservationDate,
        formData.reservationTime,
        reservation?.id,
        RESERVATION_TABLE_BUFFER_MINUTES,
      ),
    [tables, reservations, formData.reservationDate, formData.reservationTime, reservation?.id]
  );

  const tablesFittingParty = useMemo(
    () =>
      getTablesFittingParty(
        tables,
        reservations,
        formData.reservationDate,
        formData.reservationTime,
        formData.partySize,
        reservation?.id,
        RESERVATION_TABLE_BUFFER_MINUTES,
      ),
    [
      tables,
      reservations,
      formData.reservationDate,
      formData.reservationTime,
      formData.partySize,
      reservation?.id,
    ]
  );

  const totalCapacity = useMemo(
    () =>
      getTotalAvailableCapacity(
        tables,
        reservations,
        formData.reservationDate,
        formData.reservationTime,
        eventMaxCapacity,
        reservation?.id
      ),
    [
      tables,
      reservations,
      formData.reservationDate,
      formData.reservationTime,
      eventMaxCapacity,
      reservation?.id,
    ]
  );

  const maxSingleCapacity = useMemo(() => getMaxSingleTableCapacity(tables), [tables]);
  const selectedTable = formData.tableId ? tables.find((t) => t.id === formData.tableId) ?? null : null;
  const tableFits = selectedTable ? tableFitsParty(selectedTable, formData.partySize) : false;
  const needsCapacityCheck =
    !formData.tableId || formData.partySize > maxSingleCapacity;
  const canReserveWithMerge = capacityChecked && totalCapacity >= formData.partySize;
  const mustWaitlist = capacityChecked && totalCapacity < formData.partySize;

  const selectedJoinTables = useMemo(
    () => formData.assignedTableIds.map((id) => tables.find((t) => t.id === id)).filter(Boolean) as Table[],
    [tables, formData.assignedTableIds]
  );
  const selectedJoinCapacity = selectedJoinTables.reduce((sum, t) => sum + t.capacity, 0);
  const canSaveWithJoinTables =
    formData.assignedTableIds.length >= 2 && selectedJoinCapacity >= formData.partySize;
  const showJoinTablesSection = formData.partySize > maxSingleCapacity;

  const buffetStaffFields = (): Partial<Reservation> => {
    const out: Partial<Reservation> = {};
    if (formData.slotId.trim()) out.slotId = formData.slotId.trim();
    if (formData.adultsCountStr.trim() !== '') {
      const n = parseInt(formData.adultsCountStr, 10);
      if (Number.isFinite(n)) out.adultsCount = n;
    }
    if (formData.kidsBelow10.trim() !== '') {
      const n = parseInt(formData.kidsBelow10, 10);
      if (Number.isFinite(n)) out.kidsBelow10Count = n;
    }
    if (formData.kids10Plus.trim() !== '') {
      const n = parseInt(formData.kids10Plus, 10);
      if (Number.isFinite(n)) out.kidsAbove10Count = n;
    }
    out.foodAllergyDeclaration = formData.foodAllergy;
    out.allergyDetails =
      formData.foodAllergy === 'has_allergies'
        ? formData.allergyDetails.trim() || undefined
        : undefined;
    return out;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    commitPartySizeInput();
    if (!formData.eventId) {
      alert('Please select an event. General bookings are created from the POS Reservation tab.');
      return;
    }
    if (!formData.customerName.trim() || !formData.customerPhone.trim()) {
      alert('Please fill in Customer Name and Phone.');
      return;
    }
    if (formData.tableId && !tableFitsParty(selectedTable, formData.partySize)) {
      alert('Party size exceeds the selected table capacity. Choose a larger table or check venue capacity.');
      return;
    }
    if (canSaveWithJoinTables) {
      onSave({
        tableId: formData.assignedTableIds[0],
        assignedTableIds: formData.assignedTableIds,
        customerName: formData.customerName.trim(),
        customerPhone: formData.customerPhone.trim(),
        customerEmail: formData.customerEmail?.trim() || undefined,
        partySize: formData.partySize,
        reservationDate: formData.reservationDate,
        reservationTime: formData.reservationTime,
        status: (formData.status as Reservation['status']) || 'pending',
        specialRequests: formData.specialRequests.trim() || undefined,
        notes: (formData.notes.trim() || `Tables to join: ${selectedJoinTables.map((t) => t.name).join(', ')}`).trim(),
        eventId: formData.eventId ? (Number(formData.eventId) || formData.eventId) : undefined,
        requiresMerge: true,
        ...buffetStaffFields(),
      });
      return;
    }
    if (formData.tableId && tableFits && formData.assignedTableIds.length === 0) {
      onSave({
        tableId: formData.tableId,
        assignedTableIds: [],
        customerName: formData.customerName.trim(),
        customerPhone: formData.customerPhone.trim(),
        customerEmail: formData.customerEmail?.trim() || undefined,
        partySize: formData.partySize,
        reservationDate: formData.reservationDate,
        reservationTime: formData.reservationTime,
        status: (formData.status as Reservation['status']) || 'pending',
        specialRequests: formData.specialRequests.trim() || undefined,
        notes: formData.notes.trim() || undefined,
        eventId: formData.eventId ? (Number(formData.eventId) || formData.eventId) : undefined,
        requiresMerge: false,
        ...buffetStaffFields(),
      });
      return;
    }
    if (saveMode === 'merge' && canReserveWithMerge) {
      onSave({
        tableId: 0,
        customerName: formData.customerName.trim(),
        customerPhone: formData.customerPhone.trim(),
        customerEmail: formData.customerEmail?.trim() || undefined,
        partySize: formData.partySize,
        reservationDate: formData.reservationDate,
        reservationTime: formData.reservationTime,
        status: 'pending',
        specialRequests: formData.specialRequests.trim() || undefined,
        notes: (formData.notes.trim() || 'Staff will merge tables on arrival.').trim(),
        eventId: formData.eventId ? (Number(formData.eventId) || formData.eventId) : undefined,
        requiresMerge: true,
        assignedTableIds: [],
        ...buffetStaffFields(),
      });
      return;
    }
    if (saveMode === 'waitlist' || mustWaitlist) {
      onSave({
        tableId: 0,
        customerName: formData.customerName.trim(),
        customerPhone: formData.customerPhone.trim(),
        customerEmail: formData.customerEmail?.trim() || undefined,
        partySize: formData.partySize,
        reservationDate: formData.reservationDate,
        reservationTime: formData.reservationTime,
        status: 'waitlist',
        specialRequests: formData.specialRequests.trim() || undefined,
        notes: formData.notes.trim() || undefined,
        eventId: formData.eventId ? (Number(formData.eventId) || formData.eventId) : undefined,
        requiresMerge: false,
        ...buffetStaffFields(),
      });
      return;
    }
    if (needsCapacityCheck && !capacityChecked) {
      setCapacityChecked(true);
      return;
    }
    if (needsCapacityCheck && capacityChecked) {
      if (canReserveWithMerge) setSaveMode('merge');
      else setSaveMode('waitlist');
      return;
    }
    alert('Please select a table that fits the party size, or check venue capacity.');
  };

  const handlePrimaryAction = (e: React.FormEvent) => {
    e.preventDefault();
    handleSubmit(e);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          {reservation ? 'Edit Reservation' : 'New Reservation'}
        </h2>

        <form onSubmit={handlePrimaryAction} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Event</label>
            <select
              value={formData.eventId}
              onChange={(e) => setFormData({ ...formData, eventId: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
            >
              <option value="">Select event…</option>
              {events
                .filter((e) => eventAppliesToReservationDate(e, formData.reservationDate))
                .map((ev) => (
                  <option key={String(ev.id)} value={String(ev.id)}>
                    {ev.name} ({ev.eventDate})
                  </option>
                ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">General (walk-in) bookings are created from the POS Reservation tab.</p>
          </div>

          {selectedEvent?.mealSlots && selectedEvent.mealSlots.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Meal slot</label>
              <select
                value={formData.slotId}
                onChange={(e) => {
                  const id = e.target.value;
                  const slot = selectedEvent.mealSlots?.find((s) => s.id === id);
                  setFormData({
                    ...formData,
                    slotId: id,
                    ...(slot ? { reservationTime: slot.startTime } : {}),
                  });
                  setCapacityChecked(false);
                  setSaveMode(null);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
              >
                <option value="">Not set</option>
                {selectedEvent.mealSlots.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label} ({s.startTime}
                    {s.endTime ? `–${s.endTime}` : ''})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Adults #</label>
              <input
                type="number"
                min={0}
                placeholder="—"
                value={formData.adultsCountStr}
                onChange={(e) => setFormData({ ...formData, adultsCountStr: e.target.value })}
                className="w-full px-2 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Kids &lt;10</label>
              <input
                type="number"
                min={0}
                value={formData.kidsBelow10}
                onChange={(e) => setFormData({ ...formData, kidsBelow10: e.target.value })}
                className="w-full px-2 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Kids 10+</label>
              <input
                type="number"
                min={0}
                value={formData.kids10Plus}
                onChange={(e) => setFormData({ ...formData, kids10Plus: e.target.value })}
                className="w-full px-2 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Allergies</label>
              <select
                value={formData.foodAllergy}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    foodAllergy: e.target.value as 'none' | 'has_allergies',
                  })
                }
                className="w-full px-2 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="none">None declared</option>
                <option value="has_allergies">Has allergies</option>
              </select>
            </div>
          </div>
          {formData.foodAllergy === 'has_allergies' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Allergy details</label>
              <textarea
                value={formData.allergyDetails}
                onChange={(e) => setFormData({ ...formData, allergyDetails: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Party size <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={partySizeInput}
                data-soft-keyboard-handled="true"
                readOnly={partySizeKeyboard.readOnly}
                onFocus={partySizeKeyboard.onFocus}
                onChange={partySizeKeyboard.onChange}
                onBlur={commitPartySizeInput}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Table (optional)</label>
              <select
                value={formData.assignedTableIds.length >= 2 ? 0 : formData.tableId}
                onChange={(e) => {
                  const id = parseInt(e.target.value, 10) || 0;
                  setFormData({
                    ...formData,
                    tableId: id,
                    assignedTableIds: id ? [] : formData.assignedTableIds,
                  });
                  setCapacityChecked(false);
                  setSaveMode(null);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
              >
                <option value={0}>No table / Check venue capacity</option>
                {freeTables.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} (Capacity: {t.capacity})
                  </option>
                ))}
              </select>
            </div>
          </div>

          {formData.tableId && selectedTable && !tableFits && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
              Party size ({formData.partySize}) exceeds this table&apos;s capacity ({selectedTable.capacity}).
              Choose a larger table or leave &quot;No table&quot; to check venue capacity.
            </div>
          )}

          {showJoinTablesSection && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
              <label className="block text-sm font-medium text-gray-800 mb-2">
                Tables to join (select 2 or more)
              </label>
              <p className="text-xs text-gray-600 mb-2">
                Party of {formData.partySize} needs more than one table. Select which tables to push together so staff know at service time.
              </p>
              <div className="flex flex-wrap gap-3">
                {freeTables.map((t) => {
                  const checked = formData.assignedTableIds.includes(t.id);
                  return (
                    <label
                      key={t.id}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const next = checked
                            ? formData.assignedTableIds.filter((id) => id !== t.id)
                            : [...formData.assignedTableIds, t.id];
                          setFormData({
                            ...formData,
                            assignedTableIds: next,
                            tableId: next.length >= 2 ? 0 : formData.tableId,
                          });
                          setCapacityChecked(false);
                          setSaveMode(null);
                        }}
                        className="rounded border-gray-300 text-pink-600 focus:ring-pink-500"
                      />
                      <span className="text-sm text-gray-800">
                        {t.name} ({t.capacity})
                      </span>
                    </label>
                  );
                })}
              </div>
              {formData.assignedTableIds.length > 0 && (
                <p className="text-sm text-gray-700 mt-2">
                  Selected: {selectedJoinTables.map((t) => t.name).join(', ')}
                  {' '}(total {selectedJoinCapacity} seats)
                  {formData.assignedTableIds.length >= 2 && selectedJoinCapacity < formData.partySize && (
                    <span className="text-amber-700 ml-1">— need {formData.partySize - selectedJoinCapacity} more seats.</span>
                  )}
                </p>
              )}
              {canSaveWithJoinTables && (
                <p className="text-sm text-green-700 mt-1 font-medium">
                  ✓ Capacity OK. Save reservation to assign these tables.
                </p>
              )}
            </div>
          )}

          {needsCapacityCheck && (
            <div className="space-y-2">
              {!capacityChecked ? (
                <button
                  type="button"
                  onClick={() => setCapacityChecked(true)}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 border border-gray-300"
                >
                  Check venue capacity
                </button>
              ) : (
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-md text-sm">
                  <p className="font-medium text-gray-800">Available capacity: {totalCapacity} seats</p>
                  {canReserveWithMerge && (
                    <p className="text-green-700 mt-1">
                      You can reserve and staff will merge tables on arrival.
                    </p>
                  )}
                  {mustWaitlist && (
                    <p className="text-amber-700 mt-1">No capacity – add to waiting list.</p>
                  )}
                </div>
              )}
              {(canReserveWithMerge || mustWaitlist) && capacityChecked && (
                <div className="flex gap-2">
                  {canReserveWithMerge && (
                    <button
                      type="button"
                      onClick={() => setSaveMode('merge')}
                      className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                    >
                      Reserve (merge tables on arrival)
                    </button>
                  )}
                  {mustWaitlist && (
                    <button
                      type="button"
                      onClick={() => setSaveMode('waitlist')}
                      className="flex-1 px-4 py-2 bg-amber-500 text-white rounded-md hover:bg-amber-600"
                    >
                      Add to waiting list
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formData.reservationDate}
                onChange={(e) => {
                  setFormData({ ...formData, reservationDate: e.target.value });
                  setCapacityChecked(false);
                  setSaveMode(null);
                }}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Time <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={formData.reservationTime}
                onChange={(e) => {
                  setFormData({ ...formData, reservationTime: e.target.value });
                  setCapacityChecked(false);
                  setSaveMode(null);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Customer name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.customerName}
              onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={formData.customerPhone}
                onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email <span className="text-gray-500">(for confirmation)</span>
              </label>
              <input
                type="email"
                value={formData.customerEmail}
                onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
                placeholder="Required to send confirmation"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as Reservation['status'] })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
            >
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="waitlist">Waitlist</option>
              <option value="seated">Seated</option>
              <option value="cancelled">Cancelled</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Special requests</label>
            <textarea
              value={formData.specialRequests}
              onChange={(e) => setFormData({ ...formData, specialRequests: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
              placeholder="Dietary restrictions, seating preferences..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
              placeholder="Internal notes; e.g. merge tables when party arrives"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-pink-500 text-white rounded-md hover:bg-pink-600"
            >
              {reservation ? 'Update' : 'Create'} reservation
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReservationModal;
